// [목적] /notifications — 알림함 (전체/소셜/공지 탭 + 미읽 카운트 + 모두 읽음 + 무한스크롤)
// [이유] fe-be-gap-tier3 T9 — SEED mock 제거, 실데이터 훅(useNotifications/useMarkNotificationRead/useMarkAllRead) 연결.
//         ADR-T3-3: 소셜 알림 문구는 FE 조립(type+actor_handle+target), system/badge 는 BE content 사용.
//         ADR-T3-2: tab=all/social/system 연결 (social=like|fork|follow|comment, system=system|badge).
//         M7 P2(발견 화면 필터+비공개 프로필): follow 알림에 "맞팔로우" 버튼 추가(FollowBackButton) —
//         알림 목록에는 isFollowing 상태가 없어 항상 follow() 시도(BE idempotent, 이미 팔로우 중이면
//         alreadyFollowing=true 로 안전하게 무시). comment 알림 클릭 시 /folio/:id#comments 로 이동
//         (FolioPage 가 해시로 스크롤하지 않으면 단순 이동만 — comments 섹션 자동 포커스는 별도 Spec).
// [사이드 이팩트] target_type='folio' 클릭 시 /folio/:id 라우팅(comment 타입은 #comments 해시 추가). system/badge 는 content 표시.
//                 unreadCount 는 Sidebar 뱃지와 동일 훅(useUnreadCount) 로 갱신됨.
//                 FollowBackButton 은 user.client.ts follow() 직접 호출 — useFollowToggle 은 "현재 팔로우 여부"를
//                 전제로 한 토글이라 알림 목록(상태 미보유)에는 맞지 않아 별도 단순 mutation 사용.
// [수정 시 고려사항] WebSocket 실시간 업데이트는 별도 Spec(ADR-T3-6 out-of-scope).
//                   pages/auth/** 쿨톤은 의도된 전용 톤(ADR-DA6) — 절대 변경 금지.
//                   집계 알림("외 N명")은 out-of-scope(후속 Spec).

import { Box, ButtonBase, CircularProgress, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { type NotificationTab, type NotificationItem } from '@gangwoncanvas/types';

import { LineIcon } from '../components/icons/LineIcons.js';
import { NotificationRow } from '../components/notifications/notificationShared.js';
import {
  useNotifications,
  useUnreadCount,
  useMarkNotificationRead,
  useMarkAllRead,
} from '../hooks/useNotifications.js';

// [목적] paper/ink/line/brand 정본 토큰 바인딩 — W2(design-alignment) 쿨톤 정본화(R-9)
// [이유] ADR-TR8: 신규 토큰 추가 없이 paper/ink/line 만 사용. [ADR-TR8 예외는 2026-09-18 폐기 — ink.low(#6F6A5C) 는 AA 충족이라 텍스트 사용 가능]
//         TEXT_HI: ink.high(#29261B) ← #14181F
//         TEXT_MID: ink.mid(#5C5849) ← #586069
//         TEXT_LOW: ink.low(#6F6A5C) ← #8B949E — 보조 텍스트 한정(비본문)
//         BORDER: line.base(#E8E2D5) ← #E1E7EF
//         BG1: paper[1](#FAF9F7) ← #F6F8FB
//         BRAND: brand[500] → 토큰 참조 전환(값 동일 #1F6FEB)
// [사이드 이팩트] 알림 미읽 배경 rgba(31,111,235,.04) 는 BRAND 기반이므로 그대로 유지
// [수정 시 고려사항] ink.low(#6F6A5C) 는 시간 표기(보조 텍스트) — 본문 미사용(ADR-TR8). [ADR-TR8 예외는 2026-09-18 폐기 — ink.low(#6F6A5C) 는 AA 충족이라 텍스트 사용 가능]
//                   contrast.spec.ts 에서 paper[1] 위 ink.high/mid 4.5:1 이상 게이트 검증됨.
const c = tokens.color;
const L = 'light' as const;
const TEXT_HI = c.ink.high[L]; // #29261B (← 하드코딩 #14181F)
const TEXT_MID = c.ink.mid[L]; // #5C5849 (← 하드코딩 #586069)
const TEXT_LOW = c.ink.low[L]; // #6F6A5C — 보조 텍스트 한정(ADR-TR8, 본문 미사용)
const BORDER = c.line.base[L]; // #E8E2D5 (← 하드코딩 #E1E7EF)
const BG1 = c.paper[1][L]; // #FAF9F7 (← 하드코딩 #F6F8FB)
const BRAND = c.brand[500][L]; // #1F6FEB (하드코딩 → 토큰 참조)
// [목적] 소형 브랜드 텍스트(11~14px) 전용 — "모두 읽음"·탭 hover 등
// [이유] brand[500] 은 소형 텍스트에서 WCAG AA 미달(tokens/color.ts brand[600] 주석 참조)
const BRAND_TEXT = c.brand[600][L];

/**
 * [목적] 알림 페이지 본문 최대 폭 — 헤더·탭·리스트 공통 정렬축
 * [이유] 2026-09-20: max-width 가 없어 모바일 전제 레이아웃이 1513px 데스크톱에 그대로 펼쳐졌다.
 *        알림은 단일 컬럼 피드라 720px 면 한 행이 너무 길어지지 않고 시선 이동도 짧다.
 * [수정 시 고려사항] sticky 배경 바 자체는 전폭 유지하고 내용물만 이 값으로 제한한다 —
 *                   바 전체를 좁히면 스크롤 시 상단 경계가 끊겨 보인다.
 */
const CONTENT_MAX_WIDTH = 720;

// ─── 탭 정의 ──────────────────────────────────────────────────────────────────

const TABS: ReadonlyArray<{ id: NotificationTab; label: string }> = [
  { id: 'all', label: '전체' },
  { id: 'social', label: '소셜' },
  { id: 'system', label: '공지' },
];

// ─── 페이지 ───────────────────────────────────────────────────────────────────

export function NotificationsPage(): JSX.Element {
  const [tab, setTab] = useState<NotificationTab>('all');

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isError } =
    useNotifications(tab);

  const unreadCount = useUnreadCount();
  const { mutate: markRead } = useMarkNotificationRead();
  const { mutate: markAllRead, isPending: isMarkingAll } = useMarkAllRead();

  // 전체 아이템 플래튼 (무한스크롤 페이지 합산)
  const allItems: NotificationItem[] = data?.pages.flatMap((p) => p.items) ?? [];

  const handleMarkAllRead = useCallback((): void => {
    if (unreadCount === 0) return;
    markAllRead();
  }, [unreadCount, markAllRead]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: BG1 }}>
      {/* 헤더 */}
      <Box
        sx={{
          bgcolor: c.paper[0][L],
          borderBottom: `1px solid ${BORDER}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        {/* [목적] 헤더 내용물 — CONTENT_MAX_WIDTH 로 중앙 정렬
            [이유] 2026-09-20 notification-bell: 이 페이지는 모바일 전제 레이아웃을 max-width 없이
                   그대로 데스크톱에 펼쳐, 제목은 왼쪽 끝·"모두 읽음"은 1600px 오른쪽 끝에 떨어져
                   둘이 무관한 UI 처럼 보였다. 배경 바(sticky)는 전폭 유지하되 내용만 중앙 정렬한다.
            [수정 시 고려사항] 헤더·탭·리스트 세 블록이 모두 같은 상수를 써야 좌측 정렬축이 맞는다. */}
        <Box
          sx={{
            maxWidth: CONTENT_MAX_WIDTH,
            mx: 'auto',
            p: '18px 20px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '9px' }}>
            <Typography
              component="h1"
              sx={{
                fontSize: 26,
                fontWeight: 800,
                color: TEXT_HI,
                letterSpacing: '-0.6px',
                m: 0,
              }}
            >
              알림
            </Typography>
            {unreadCount > 0 && (
              <Box
                component="span"
                aria-label={`미읽 알림 ${unreadCount}개`}
                sx={{
                  bgcolor: BRAND,
                  color: c.paper[0][L],
                  fontSize: 11,
                  fontWeight: 800,
                  borderRadius: 99,
                  px: '9px',
                  py: '3px',
                  fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                }}
              >
                {unreadCount}
              </Box>
            )}
          </Box>
          <ButtonBase
            onClick={handleMarkAllRead}
            disabled={unreadCount === 0 || isMarkingAll}
            aria-label="모두 읽음"
            sx={{
              color: unreadCount > 0 ? BRAND_TEXT : TEXT_LOW,
              fontSize: 12.5,
              fontWeight: 700,
              border: `1px solid ${BORDER}`,
              borderRadius: 99,
              px: '16px',
              py: '8px',
              bgcolor: c.paper[0][L],
              transition: `all ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
              '&:hover': { borderColor: BRAND },
              '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
              '&:disabled': { opacity: 0.45 },
            }}
          >
            모두 읽음
          </ButtonBase>
        </Box>

        {/* [목적] 탭 — 얇은 밑줄 탭 → 세그먼트 pill
            [이유] 2026-09-20: 12px 텍스트 + 2px 밑줄은 데스크톱 폭에서 클릭 타겟이 지나치게 작고,
                   발견 페이지 필터칩·벨 드롭다운 탭과 시각 언어가 어긋난다. pill 로 통일.
            [사이드 이팩트] role/aria-selected/aria-current 는 기존 그대로 — 접근성·테스트 호환. */}
        <Box
          role="tablist"
          aria-label="알림 탭"
          sx={{
            maxWidth: CONTENT_MAX_WIDTH,
            mx: 'auto',
            display: 'flex',
            gap: '8px',
            px: '20px',
            pb: '14px',
          }}
        >
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <ButtonBase
                key={t.id}
                role="tab"
                onClick={() => setTab(t.id)}
                aria-label={`탭 ${t.label}`}
                aria-selected={active}
                aria-current={active ? 'page' : undefined}
                sx={{
                  px: '18px',
                  height: 38,
                  borderRadius: 99,
                  fontSize: 13,
                  fontWeight: active ? 700 : 600,
                  bgcolor: active ? TEXT_HI : c.paper[0][L],
                  color: active ? c.paper[0][L] : TEXT_MID,
                  border: `1px solid ${active ? TEXT_HI : BORDER}`,
                  boxShadow: active ? '0 3px 10px rgba(41,38,27,.22)' : 'none',
                  transition: `all ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
                  '&:hover': active ? {} : { borderColor: BRAND, color: BRAND_TEXT },
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                {t.label}
              </ButtonBase>
            );
          })}
        </Box>
      </Box>

      {/* 리스트 영역 — 헤더와 동일한 정렬축(CONTENT_MAX_WIDTH) */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          width: '100%',
          maxWidth: CONTENT_MAX_WIDTH,
          mx: 'auto',
          p: '22px 20px 60px',
        }}
      >
        {/* 로딩 상태 */}
        {isPending && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: '40px' }}>
            <CircularProgress size={24} sx={{ color: BRAND }} aria-label="알림 로딩 중" />
          </Box>
        )}

        {/* 에러 상태 */}
        {isError && !isPending && (
          <Box sx={{ p: '60px 20px', textAlign: 'center' }}>
            <Box sx={{ fontSize: 40, mb: '10px', opacity: 0.5 }}>⚠️</Box>
            <Typography sx={{ fontSize: 12, color: TEXT_MID, fontWeight: 600 }}>
              알림을 불러오지 못했어요
            </Typography>
          </Box>
        )}

        {/* 빈 상태 — 아래 EmptyNotifications 참조 */}
        {!isPending && !isError && allItems.length === 0 && <EmptyNotifications />}

        {/* 알림 목록 — 행 디자인은 notificationShared.NotificationRow 가 단일 소스(벨 드롭다운과 공유) */}
        {!isPending &&
          allItems.map((item) => (
            <NotificationRow
              key={item.id}
              item={item}
              density="page"
              onMarkRead={(id) => markRead(id)}
            />
          ))}

        {/* 무한스크롤 — 더 보기 버튼 */}
        {hasNextPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: '10px' }}>
            <ButtonBase
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
              aria-label="알림 더 보기"
              sx={{
                px: '22px',
                py: '10px',
                borderRadius: 99,
                bgcolor: c.paper[0][L],
                border: `1px solid ${BORDER}`,
                fontSize: 12.5,
                color: TEXT_MID,
                fontWeight: 700,
                '&:hover': { borderColor: BRAND, color: BRAND_TEXT },
                '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                '&:disabled': { opacity: 0.5 },
              }}
            >
              {isFetchingNextPage ? '로딩 중…' : '더 보기'}
            </ButtonBase>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─── 빈 상태 ──────────────────────────────────────────────────────

/**
 * [목적] 알림 0건 빈 상태 — 왜 비어있는지 설명 + 다음 행동 2개 제시
 * [이유] 2026-09-20: 기존은 🔔 이모지 1개 + "알림이 없어요" 한 줄이 1183px 높이 영역 맨 위에
 *        붙어 있어 화면이 고장난 것처럼 보였다(사용자 제보 스크린샷). 빈 상태는 "무엇을 하면
 *        알림이 오는지"를 알려주는 자리다.
 * [사이드 이팩트] 없음 — 네비게이션만 수행.
 * [수정 시 고려사항] 벨 드롭다운의 빈 상태는 공간이 좁아 더 압축된 별도 버전을 쓴다
 *                   (NotificationBell.tsx) — 이러므로 두 문구를 바꿀 땐 양쪽을 함께 볼 것.
 */
function EmptyNotifications(): JSX.Element {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        bgcolor: c.paper[0][L],
        border: `1px solid ${BORDER}`,
        borderRadius: `${tokens.radius.xl}px`,
        p: '56px 32px 44px',
        textAlign: 'center',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(160deg, rgba(31,111,235,.05), rgba(43,182,115,.04) 50%, transparent 75%)',
          pointerEvents: 'none',
        }}
      />
      <Box sx={{ position: 'relative' }}>
        <Box
          aria-hidden="true"
          sx={{
            width: 92,
            height: 92,
            borderRadius: '50%',
            mx: 'auto',
            mb: '20px',
            background: 'linear-gradient(145deg,#E3EDFB,#EAF3EC)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: BRAND,
          }}
        >
          <LineIcon name="notifications" size={42} />
        </Box>
        <Typography
          sx={{ fontSize: 19, fontWeight: 800, color: TEXT_HI, letterSpacing: '-0.4px', mb: '8px' }}
        >
          아직 새 알림이 없어요
        </Typography>
        <Typography
          sx={{
            fontSize: 13,
            color: TEXT_LOW,
            lineHeight: 1.65,
            maxWidth: 360,
            mx: 'auto',
            mb: '24px',
          }}
        >
          다른 여행자가 내 폴리오에 좋아요·댓글을 남기거나
          <br />
          나를 팔로우하면 여기에 모아서 알려드려요.
        </Typography>
        <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <ButtonBase
            onClick={() => navigate('/explore')}
            aria-label="폴리오 둘러보기"
            sx={{
              bgcolor: BRAND,
              color: c.paper[0][L],
              fontSize: 13,
              fontWeight: 700,
              px: '22px',
              py: '11px',
              borderRadius: 99,
              '&:focus-visible': { outline: `2px solid ${TEXT_HI}`, outlineOffset: 2 },
            }}
          >
            폴리오 둘러보기
          </ButtonBase>
          <ButtonBase
            onClick={() => navigate('/settings')}
            aria-label="알림 설정"
            sx={{
              bgcolor: c.paper[0][L],
              color: TEXT_HI,
              border: `1px solid ${BORDER}`,
              fontSize: 13,
              fontWeight: 700,
              px: '22px',
              py: '11px',
              borderRadius: 99,
              '&:hover': { borderColor: BRAND },
              '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
            }}
          >
            알림 설정
          </ButtonBase>
        </Box>
      </Box>
    </Box>
  );
}
